// @ts-nocheck
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  useTheme,
  Card,
  CardContent,
} from '@mui/material';
import Diversity2Icon from '@mui/icons-material/Diversity2';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import ReactMuiTableListView from '../ReactMuiTableListViewV8';
import useDocumentInventory from '../../../hooks/ModelInventory/useDocumentInventory';
import {
  constructBreadcrumbState,
  getEntityTypeKey,
  getEntityTypes,
  getLabelPathFromLabelConfig,
} from '../../../utils/Utils';
import { DEFAULT_ROUTE_CONFIG } from '../../../const/defaultRoutesConfig';
import { ReactComponent as ModelIcon } from '../../../assets/images/ModelDetails/ModelLogo.svg';
import { ReactComponent as ArtifactIcon } from '../../../assets/Icons/ArtifactsIcon.svg';
import { ReactComponent as GitPullRequest } from '../../../assets/images/ModelDetails/GitPullRequest.svg';
import { ReactComponent as QueryIcon } from '../../../assets/Icons/Query 3.svg';
import { ReactComponent as InfoIcon } from '../../../assets/Icons/infoIcon.svg';

const CustomEntityIdCell = ({ row, column }) => {
  const { getEntityIcon } = column;
  const entity = row?.original;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {getEntityIcon(entity)}
      <Typography variant="body2">{entity?.unique_id}</Typography>
    </Stack>
  );
};

CustomEntityIdCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      unique_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
  column: PropTypes.shape({
    getEntityIcon: PropTypes.func,
  }).isRequired,
};

const CustomEntityNameCell = ({ row }) => {
  const entity = row?.original;
  return (
    <Typography variant="body2">
      {entity?.entity_name || entity?.unique_id}
    </Typography>
  );
};

CustomEntityNameCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      unique_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      entity_name: PropTypes.string,
    }),
  }).isRequired,
};

const DocumentLinkedEntities = ({ documentId, onCloseModal }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [entities, setEntities] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const { getDocumentLinkedEntities } = useDocumentInventory();

  useEffect(() => {
    if (documentId) {
      setLoading(true);
      getDocumentLinkedEntities({
        document_id: documentId,
        pageIndex: pageIndex + 1,
        pageSize: pageSize,
      })
        .then((response) => {
          if (response?.data) {
            setEntities(response.data.entities || []);
            setCount(response.data.count || 0);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [documentId, pageIndex, pageSize]);

  const handleFetchData = useCallback((_pageIndex, _pageSize) => {
    if (_pageIndex === 0) {
      setSkipPageReset(false);
    } else {
      setSkipPageReset(true);
    }
    setPageIndex(_pageIndex);
    setPageSize(_pageSize);
  }, []);

  const redirectToModel = useCallback(
    (entity) => {
      const uniqueId = entity?.unique_id;

      const entityTypeKey = getEntityTypeKey(
        entity?.entity_type,
        entity?.request_type
      );

      const dynamicEntityPath = getLabelPathFromLabelConfig(
        DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.keyPath || '',
        'path',
        DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.path
      );

      const breadcrumbState = constructBreadcrumbState(
        entity,
        [],
        true,
        uniqueId,
        dynamicEntityPath,
        getEntityTypes(entity?.entity_type, entity?.request_type)
      );

      navigate(
        {
          pathname: `/${dynamicEntityPath}/${uniqueId}`,
        },
        {
          state: breadcrumbState,
        }
      );
    },
    [navigate]
  );

  const getEntityIcon = useCallback(
    (entity) => {
      const { entity_type, request_type } = entity;

      if (entity_type === 'ModelInventory') {
        if (request_type === 'ModelRequest') {
          const modelRequestLabel = getLabelPathFromLabelConfig(
            `TEMPLATE_TYPES.ModelRequest`,
            'featureLabel',
            'Assessment'
          );
          return (
            <Box
              title={modelRequestLabel}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <GitPullRequest
                style={{
                  width: '16px',
                  height: '16px',
                  color: theme?.palette?.entityColor?.assessment,
                }}
              />
            </Box>
          );
        }
        if (request_type === 'UseCase') {
          const useCaseLabel = getLabelPathFromLabelConfig(
            `TEMPLATE_TYPES.UseCase`,
            'featureLabel',
            'UseCase'
          );
          return (
            <Box
              title={useCaseLabel}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Diversity2Icon
                style={{
                  width: '16px',
                  height: '16px',
                  color: theme?.palette?.entityColor?.useCase,
                }}
              />
            </Box>
          );
        }
        if (request_type === 'Query') {
          const queryLabel = getLabelPathFromLabelConfig(
            `TEMPLATE_TYPES.Query`,
            'featureLabel',
            'Query'
          );
          return (
            <Box
              title={queryLabel}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <QueryIcon
                style={{
                  width: '16px',
                  height: '16px',
                  color: theme?.palette?.entityColor?.query,
                }}
              />
            </Box>
          );
        }
        if (request_type === 'AccessRequest') {
          const accessRequestLabel = getLabelPathFromLabelConfig(
            `TEMPLATE_TYPES.AccessRequest`,
            'featureLabel',
            'AccessRequest'
          );
          return (
            <Box
              title={accessRequestLabel}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <VpnKeyIcon
                style={{
                  width: '16px',
                  height: '16px',
                  color: theme?.palette?.entityColor?.accessRequest,
                }}
              />
            </Box>
          );
        }
        const modelInventoryLabel = getLabelPathFromLabelConfig(
          `TEMPLATE_TYPES.ModelInventory`,
          'featureLabel',
          'Model'
        );
        return (
          <Box
            title={modelInventoryLabel}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ModelIcon
              style={{
                width: '16px',
                height: '16px',
                color: theme?.palette?.entityColor?.model,
              }}
            />
          </Box>
        );
      }

      const modelAssociationLabel = getLabelPathFromLabelConfig(
        `TEMPLATE_TYPES.ModelAssociation`,
        'featureLabel',
        'Artifact'
      );

      if (entity_type === 'ModelAssociation') {
        return (
          <Box
            title={modelAssociationLabel}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ArtifactIcon
              style={{
                width: '16px',
                height: '16px',
              }}
            />
          </Box>
        );
      }

      return (
        <ModelIcon
          style={{
            width: '16px',
            height: '16px',
            color: theme?.palette?.entityColor?.model,
          }}
        />
      );
    },
    [theme, getLabelPathFromLabelConfig]
  );

  const columns = useMemo(
    () => [
      {
        heading: 'Entity ID',
        Header: 'Entity ID',
        accessor: 'unique_id',
        width: 200,
        getEntityIcon: getEntityIcon,
        Cell: CustomEntityIdCell,
      },
      {
        heading: 'Entity Names',
        Header: 'Entity Names',
        accessor: 'entity_name',
        width: 200,
        Cell: CustomEntityNameCell,
      },
    ],
    [getEntityIcon]
  );

  const handleGetCellProps = useCallback(
    (cell) => {
      return {
        onDoubleClick: () => {
          redirectToModel(cell?.row?.original);
          if (onCloseModal) {
            onCloseModal();
          }
        },
      };
    },
    [redirectToModel, onCloseModal]
  );

  const handleGetRowProps = useCallback(() => ({}), []);

  if (loading && (!entities || entities.length === 0)) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!loading && (!entities || entities.length === 0)) {
    return (
      <Box display="flex" justifyContent="center" p={2} width="100%">
        <Typography
          variant="body1"
          color="textSecondary"
          align="center"
          fontWeight="500"
        >
          No linked entities impacted.
        </Typography>
      </Box>
    );
  }

  return (
    <Card sx={{ mt: 3, width: '100%' }}>
      <CardContent>
        <Typography
          variant="body1"
          mb={1}
          color={theme.palette.documentLinkedEntities.titleText}
        >
          Impact on Associated Entities
        </Typography>

        <Box
          sx={{
            backgroundColor:
              theme.palette.documentLinkedEntities.warningBackground,
            p: 1.5,
            mb: 2,
            mt: 1,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <InfoIcon style={{ width: '18px', height: '18px' }} />
            <Box>
              <Typography variant="body1" mb={0.5}>
                Note:
              </Typography>
              <Typography
                variant="caption"
                display="list-item"
                ml={2}
                sx={{ listStyleType: 'disc' }}
              >
                This update impacts <b>{count} linked entities</b>.
              </Typography>
              <Typography
                variant="caption"
                display="list-item"
                ml={2}
                sx={{ listStyleType: 'disc' }}
              >
                Review the list below to apply the new version globally or
                customize the state for each entity.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            border: `1px solid ${theme.palette.documentLinkedEntities.borderColor}`,
            borderRadius: 1,
            mt: 2,
          }}
        >
          <ReactMuiTableListView
            data={entities}
            columns={columns}
            enablePagination={true}
            enableControlledPagination={true}
            fetchData={handleFetchData}
            dataLength={count}
            pageCount={Math.ceil(count / pageSize)}
            isAllOptionDisabled={true}
            getRowProps={handleGetRowProps}
            getCellProps={handleGetCellProps}
            maxTableHeight={200}
            initialPageSize={5}
            rowsPerPageOptions={[5, 10, 15]}
            skipPageReset={skipPageReset}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

DocumentLinkedEntities.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCloseModal: PropTypes.func,
};

export default DocumentLinkedEntities;
